import React, { useState } from 'react';
import { permissions, roles } from '../../data/adminMock';
import { Shield, Users, Check, X, Plus, Edit, Trash2 } from 'lucide-react';
import { Switch } from '../../components/ui/switch';

const AdminPermissions = () => {
  const [permissionList, setPermissionList] = useState(permissions);
  const [activeTab, setActiveTab] = useState('permissions');

  const togglePermission = (permId) => {
    setPermissionList(permissionList.map(perm =>
      perm.id === permId ? { ...perm, enabled: !perm.enabled } : perm
    ));
  };

  const categories = [...new Set(permissionList.map(p => p.category))];

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">Permissions & Roles</h1>
          <p className="text-gray-400">Manage access control and user roles</p>
        </div>
        <button className="flex items-center gap-2 bg-teal-400 hover:bg-teal-500 text-black px-4 py-2 rounded-lg font-medium transition-colors">
          <Plus className="w-5 h-5" />
          Add Role
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-8">
        <button
          onClick={() => setActiveTab('permissions')}
          className={`px-6 py-3 rounded-lg font-medium transition-all ${
            activeTab === 'permissions'
              ? 'bg-teal-400/10 text-teal-400 border border-teal-400/30'
              : 'text-gray-400 hover:text-white hover:bg-[#1a1a1f]'
          }`}
        >
          <span className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Permissions
          </span>
        </button>
        <button
          onClick={() => setActiveTab('roles')}
          className={`px-6 py-3 rounded-lg font-medium transition-all ${
            activeTab === 'roles'
              ? 'bg-teal-400/10 text-teal-400 border border-teal-400/30'
              : 'text-gray-400 hover:text-white hover:bg-[#1a1a1f]'
          }`}
        >
          <span className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Roles
          </span>
        </button>
      </div>

      {activeTab === 'permissions' ? (
        /* Permissions View */
        <div className="space-y-6">
          {categories.map((category) => (
            <div key={category} className="bg-[#0d1117] border border-gray-800 rounded-xl overflow-hidden">
              <div className="bg-[#1a1a1f] px-6 py-4 border-b border-gray-800">
                <h3 className="text-white font-semibold">{category}</h3>
              </div>
              <div className="divide-y divide-gray-800">
                {permissionList
                  .filter(p => p.category === category)
                  .map((permission) => (
                    <div key={permission.id} className="flex items-center justify-between px-6 py-4">
                      <div>
                        <p className="text-white font-medium">{permission.name}</p>
                        <p className="text-gray-500 text-sm">{permission.description}</p>
                      </div>
                      <Switch
                        checked={permission.enabled}
                        onCheckedChange={() => togglePermission(permission.id)}
                        className="data-[state=checked]:bg-teal-400"
                      />
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Roles View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {roles.map((role) => (
            <div
              key={role.id}
              className="bg-[#0d1117] border border-gray-800 rounded-xl p-6 hover:border-teal-400/30 transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  role.color === 'red' ? 'bg-red-400/10' :
                  role.color === 'orange' ? 'bg-orange-400/10' :
                  role.color === 'blue' ? 'bg-blue-400/10' :
                  role.color === 'green' ? 'bg-green-400/10' :
                  'bg-gray-400/10'
                }`}>
                  <Shield className={`w-6 h-6 ${
                    role.color === 'red' ? 'text-red-400' :
                    role.color === 'orange' ? 'text-orange-400' :
                    role.color === 'blue' ? 'text-blue-400' :
                    role.color === 'green' ? 'text-green-400' :
                    'text-gray-400'
                  }`} />
                </div>
                <div className="flex gap-2">
                  <button className="p-2 hover:bg-gray-800 rounded-lg transition-colors">
                    <Edit className="w-4 h-4 text-gray-400" />
                  </button>
                  <button className="p-2 hover:bg-gray-800 rounded-lg transition-colors">
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              </div>

              <h3 className="text-white font-semibold text-lg mb-1">{role.name}</h3>
              
              <div className="space-y-3 mt-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-sm">Users</span>
                  <span className="text-white text-sm font-medium">{role.users}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-sm">Permissions</span>
                  <span className="text-white text-sm font-medium">{role.permissions}</span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-800">
                <button className="w-full text-teal-400 text-sm hover:underline">
                  View Details
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminPermissions;
